import React, { useState, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  X,
  Download,
  FileText,
  Image,
  File,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Loader2,
  AlertCircle,
} from "lucide-react";

const DocumentViewerNew = ({
  isOpen,
  onClose,
  documentUrl,
  documentName = "Document",
  documentType = "auto", // 'auto', 'pdf', 'image', 'text', 'office'
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [detectedType, setDetectedType] = useState(null);

  useEffect(() => {
    if (isOpen && documentUrl) {
      setLoading(true);
      setError(null);
      detectDocumentType();
    }
  }, [isOpen, documentUrl]);

  const detectDocumentType = () => {
    if (!documentUrl) {
      setError("No document URL provided");
      setLoading(false);
      return;
    }

    if (documentType !== "auto") {
      setDetectedType(documentType);
      setLoading(false);
      return;
    }

    // Auto-detect based on URL or file extension
    const url = documentUrl.toLowerCase();

    if (url.includes(".pdf") || url.includes("pdf")) {
      setDetectedType("pdf");
    } else if (url.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/)) {
      setDetectedType("image");
    } else if (url.match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/)) {
      setDetectedType("office");
    } else if (url.match(/\.(txt|csv|xml|json)$/)) {
      setDetectedType("text");
    } else {
      // Try to load as image first, fallback to iframe
      const img = new Image();
      img.onload = () => setDetectedType("image");
      img.onerror = () => setDetectedType("pdf");
      img.src = documentUrl;
    }

    setLoading(false);
  };

  const handleDownload = async () => {
    try {
      // Method 1: Try direct fetch with proper CORS handling
      let blob;
      try {
        const response = await fetch(documentUrl, {
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        blob = await response.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = documentName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (fetchError) {
        console.warn("Direct fetch failed, using fallback method:", fetchError);

        // Method 2: Fallback - Open in new tab and let browser handle download
        const link = document.createElement("a");
        link.href = documentUrl;
        link.download = documentName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Download failed:", err);

      // Method 3: Last resort - just open the URL
      try {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      } catch (openError) {
        console.error("Failed to open file:", openError);
        alert(
          "Unable to download the file. Please try again or contact support."
        );
      }
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  const renderDocumentContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-600 mb-2">Failed to load document</p>
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
        </div>
      );
    }

    const commonStyles = {
      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
      transformOrigin: "center",
      transition: "transform 0.3s ease",
    };

    switch (detectedType) {
      case "image":
        return (
          <div className="flex justify-center items-center min-h-96 overflow-auto">
            <img
              src={documentUrl}
              alt={documentName}
              style={commonStyles}
              className="max-w-full max-h-full object-contain"
              onError={() => setError("Failed to load image")}
            />
          </div>
        );

      case "pdf":
        return (
          <div className="h-96 md:h-[600px]" style={commonStyles}>
            <iframe
              src={`${documentUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0 rounded"
              title={documentName}
              onError={() => setError("Failed to load PDF")}
            />
          </div>
        );

      case "office":
        return (
          <div className="h-96 md:h-[600px]">
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                documentUrl
              )}`}
              className="w-full h-full border-0 rounded"
              title={documentName}
              onError={() => setError("Failed to load Office document")}
            />
          </div>
        );

      case "text":
        return (
          <div className="h-96 md:h-[600px] overflow-auto">
            <iframe
              src={documentUrl}
              className="w-full h-full border-0 rounded bg-white"
              title={documentName}
              style={commonStyles}
              onError={() => setError("Failed to load text document")}
            />
          </div>
        );

      default:
        return (
          <div className="h-96 md:h-[600px]">
            <iframe
              src={documentUrl}
              className="w-full h-full border-0 rounded"
              title={documentName}
              onError={() => setError("Failed to load document")}
            />
          </div>
        );
    }
  };

  const getDocumentIcon = () => {
    switch (detectedType) {
      case "image":
        return <Image className="w-5 h-5" />;
      case "pdf":
        return <FileText className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[99999]">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-6xl max-h-[95vh] bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getDocumentIcon()}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 truncate max-w-md">
                  {documentName}
                </h3>
                <p className="text-sm text-gray-500 capitalize">
                  {detectedType || "Document"} Viewer
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Controls for images */}
              {detectedType === "image" && (
                <>
                  <button
                    onClick={handleZoomOut}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600 min-w-[3rem] text-center">
                    {zoom}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleRotate}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                    title="Rotate"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleReset}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                    title="Reset View"
                  >
                    Reset
                  </button>
                </>
              )}

              {/* Download button */}
              {/* <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                title="Download Document"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download</span>
              </button> */}

              {/* Close button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-auto max-h-[calc(95vh-120px)]">
            {renderDocumentContent()}
          </div>

          {/* Footer with document info */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                {documentUrl && (
                  <span className="truncate max-w-md block">
                    Source: {documentUrl}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4">
                {detectedType === "image" && (
                  <span>
                    Zoom: {zoom}% | Rotation: {rotation}°
                  </span>
                )}
                <span className="capitalize">
                  {detectedType || "Unknown"} Type
                </span>
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default DocumentViewerNew;
